import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FrameSizeSelectorComponent } from '@nrwl/test-setup';
import { MatDialog } from '@angular/material/dialog';
import { CommonMaterialModule } from '@nrwl/common-material';
import { MatTableDataSource } from '@angular/material/table';
import { state, style, transition, trigger, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  REMOVED
} from '@nrwl/test-setup';
import {
  CVLAN_PRIORITY_TYPE,
  BertConfigFieldMapping,
  DSCP_CONFIG_TYPE,
  IPAddressValue,
  SVLAN_PRIORITY_TYPE,
  vlanConfigValue,
  BER_THRESHOLD_TYPES,
  ERROR_ON_OOS
} from '../../models/model';
import { isNil, cloneDeep, isEmpty } from 'lodash';
import {
  AGENT_MODEL,
  DevicesService,
  FormatService,
  TestLayerValue,
  TestMode
} from '@nrwl/common-lib';
import { Subject, Subscription } from 'rxjs';
import { BertSetupService } from '../../bert-setup.service';
@Component({
  selector: 'nrwl-bert-streams',
  standalone: true,
  imports: [CommonModule, FrameSizeSelectorComponent, CommonMaterialModule, FormsModule],
  templateUrl: './bert-streams.component.html',
  styleUrls: ['./bert-streams.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('isExpanded', style({ height: '*', visibility: 'visible' })),
      transition('isExpanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class BertStreamsComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() componentId: string;
  @Input() stateHistory: IComponentStateHistory;
  @Input() otherTabStateHistory: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  // eslint-disable-next-line
  config: any;
  // eslint-disable-next-line
  parentConfig: any;
  outputs: Array<string>;
  configMapping = BertConfigFieldMapping;
  tabsubscription: Subscription;
  agentSubscription: Subscription;
  readonly unsubscribe: Subject<void> = new Subject();
  currentRowSelected;
  disabledIcon = 'icon mdi mdi-menu-down';
  enabledIcon = 'icon mdi mdi-menu-up';
  //dropdown
  dscpConfigTypes = DSCP_CONFIG_TYPE;
  svlanPriorityTypes = SVLAN_PRIORITY_TYPE;
  cvlanPriorityTypes = CVLAN_PRIORITY_TYPE;
  errorOnOssTypes = ERROR_ON_OOS;
  // eslint-disable-next-line
  allStreamsConfig: Array<any>;
  // eslint-disable-next-line
  defaultStreamsArr: any;
  maxBandWidth = 1000;
  defaultBandWidth = 1000;
  vlanConfig = vlanConfigValue.UNTAGGED;
  ipAddressType = IPAddressValue.IP_V4;
  layer3Test = false;
  testMode = TestMode.LOOPBACK;
  agentType: string;
  selectedOriginator: any;
  defaultColumns = [];
  layer2Columns = [
    'streamName',
    'streamUlr',
    'streamFD',
    'streamFDV',
    'streamLoss',
    'streamBerThreshold',
    'streamFrame'
  ];
  layer3Columns = [
    'streamName',
    'streamDscp',
    'streamUlr',
    'streamFD',
    'streamFDV',
    'streamLoss',
    'streamBerThreshold',
    'streamFrame'
  ];
  dataSource = new MatTableDataSource();
  errorValidationObj = {
    isValidDscp: true,
    isValidName: true,
    isValidUlr: true,
    isValidFrameLoss: true,
    isValidFD: true,
    isValidFDV: true,
    isValidOverhead: true
  };
  validateAllStreams;
  streamRows = [
    {
      isExpanded: false,
      framesList: null,
      streamName: 'BERT 2^31-1',
      streamDscp: null,
      stream_svlanPriority: 0,
      stream_cvlanPriority: 0,
      streamUlr: 10,
      streamBerThreshold: null,
      streamLoss: 0,
      streamFD: 100,
      streamFDV: 100,
      streamBertFrame: 512,
      streamFrameList: null,
      streamOverheadBytes: 0,
      streamOOS: true,
      isValidConfig: true,
      sptFrameDisplay: 512,
      streamFrame: ''
    }
  ];
  berThresholdTypes = BER_THRESHOLD_TYPES;
  constructor(
    private dialog: MatDialog,
    private readonly deviceService: DevicesService,
    public setupService: BertSetupService,
    public formatService: FormatService
  ) {
    this.dataSource.data = this.streamRows;
  }

  ngOnInit(): void {
    this.restoreFormValues();
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 2) {
          this.restoreFormValues();
          this.onDataChange();
        }
      });
    }
  }
  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }

  restoreOtherTabValues(): void {
    this.vlanConfig = this.restoreEndpointsFieldValues(BertConfigFieldMapping.VLAN_CONFIG);
    this.ipAddressType = this.restoreSetupFieldValues(BertConfigFieldMapping.IP_ADDRESS_TYPE);
    this.testMode = this.restoreSetupFieldValues(BertConfigFieldMapping.TEST_MODE);
    this.agentType = this.restoreEndpointsFieldValues(BertConfigFieldMapping.AGENT_TYPE);
    this.layer3Test =
      this.restoreSetupFieldValues(BertConfigFieldMapping.LAYER_TO_TEST) === TestLayerValue.L3;
    this.selectedOriginator = this.restoreEndpointsFieldValues(
      BertConfigFieldMapping.SELECTED_ORIGINATOR
    );
  }

  restoreFormValues(): void {
    this.config = this.tab?.config;
    this.restoreOtherTabValues();
    if (this.selectedOriginator && this.config) {
      const capabilityData = this.setupService?.getAgentLookUpData(
        this.config?.lookup,
        this.selectedOriginator
      );
      this.maxBandWidth = capabilityData?.['max_bandwidth']
        ? parseInt(capabilityData?.['max_bandwidth'])
        : this.defaultBandWidth;
    }
    this.defaultStreamsArr = cloneDeep(this.restoreFieldValues(BertConfigFieldMapping.STREAMS));
    this.parentConfig = this.tab?.['parent']['output'];
    this.outputs = this.config?.def?.outputs;
    const defaultStreamRow = this.streamRows[0];
    this.streamRows = [];
    this.allStreamsConfig = cloneDeep(this.restoreFieldValues(BertConfigFieldMapping.STREAMS));
    this.streamRows.push(cloneDeep(defaultStreamRow));

    this.streamRows[0].streamName = this.allStreamsConfig[0][BertConfigFieldMapping.STREAM_NAME];
    const frameList = this.allStreamsConfig[0][BertConfigFieldMapping.FRAME_LIST];
    this.streamRows[0].framesList = frameList ?? this.getValidFrameSizes();
      this.streamRows[0].streamDscp = this.allStreamsConfig[0][BertConfigFieldMapping.TOS] ?? 184;
    this.streamRows[0].stream_svlanPriority =
      this.allStreamsConfig[0][BertConfigFieldMapping.SVLAN_PRIORITY];
    this.streamRows[0].stream_cvlanPriority =
      this.allStreamsConfig[0][BertConfigFieldMapping.CVLAN_PRIORITY];
    this.streamRows[0].streamUlr = this.allStreamsConfig[0][BertConfigFieldMapping.ULR];
    this.streamRows[0].streamBerThreshold =
      this.allStreamsConfig[0][BertConfigFieldMapping.BER_THRESHOLD];
    this.streamRows[0].streamLoss = this.allStreamsConfig[0][BertConfigFieldMapping.LOSS];
    this.streamRows[0].streamFD = this.allStreamsConfig[0][BertConfigFieldMapping.FD] || 100;
    this.streamRows[0].streamFDV = this.allStreamsConfig[0][BertConfigFieldMapping.FDV];
    this.streamRows[0].streamBertFrame =
      this.allStreamsConfig[0][BertConfigFieldMapping.BERT_FRAME_SIZE];
    this.streamRows[0].streamOverheadBytes =
      this.allStreamsConfig[0][BertConfigFieldMapping.OVER_HEAD_BYTES];
    this.streamRows[0].streamOOS = this.allStreamsConfig[0][BertConfigFieldMapping.ERROR_ON_OOS];
    this.bertFrameLabelDisplay(this.streamRows[0]);
    this.dataSource.data = this.streamRows;
    if (this.layer3Test) {
      this.defaultColumns = this.layer3Columns;
    } else {
      this.defaultColumns = this.layer2Columns;
    }
  }
  showFormField(field: string): boolean {
    return this.defaultStreamsArr?.map(item => item[field] !== undefined)[0];
  }

  //eslint-disable-next-line
  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  //eslint-disable-next-line
  restoreEndpointsFieldValues(field: string): any {
    const data = this.otherTabStateHistory('bert.endpoints.html', field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  //eslint-disable-next-line
  restoreSetupFieldValues(field: string): any {
    const data = this.otherTabStateHistory('bert.setup.html', field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  expandCollapse(row: any): void {
    if (this.currentRowSelected && this.currentRowSelected != row)
      this.currentRowSelected.isExpanded = false;
    this.currentRowSelected = row;

    if (row.isExpanded) {
      row.isExpanded = false;
    } else {
      row.isExpanded = true;
    }
  }

  getValidFrameSizes(): Array<number> {
    const singleVlanSize = this.restoreFieldValues(BertConfigFieldMapping.VALID_FRAME_SIZE_SINGLE);
    const doubleVlanSize = this.restoreFieldValues(BertConfigFieldMapping.VALID_FRAME_SIZE_DOUBLE);
    if (this.vlanConfig === vlanConfigValue.SINGLE_VLAN && singleVlanSize) {
      return singleVlanSize;
    } else if (this.vlanConfig === vlanConfigValue.Q_IN_Q && doubleVlanSize) {
      return doubleVlanSize;
    }
    const validFramesList = cloneDeep(
      this.restoreFieldValues(BertConfigFieldMapping.VALID_FRAME_SIZE)
    );
    return validFramesList;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  checkValidation(field: string, value: any, element?: any): any {
    const errMsg = { isValid: false, message: '' };
    if (field === 'name' && isEmpty(value)) {
      errMsg.isValid = true;
      errMsg.message = 'Name is required';
      this.errorValidationObj.isValidName = false;
    } else if (field === 'name') {
      this.errorValidationObj.isValidName = true;
    }
    if (field === 'ulr' && (value === null || value < 0.01 || value > this.maxBandWidth)) {
      errMsg.isValid = true;
      errMsg.message = 'Valid ulr is 0.01-' + this.maxBandWidth;
      this.errorValidationObj.isValidUlr = false;
    } else if (field === 'ulr') {
      this.errorValidationObj.isValidUlr = true;
    }
    if (field === 'loss' && (value === null || value < 0 || value > 100)) {
      errMsg.isValid = true;
      errMsg.message = 'Valid frameloss is 0-100';
      this.errorValidationObj.isValidFrameLoss = false;
    } else if (field === 'loss') {
      this.errorValidationObj.isValidFrameLoss = true;
    }
    if (field === 'fd' && (value === null || value < 0.001 || value > 1000)) {
      errMsg.isValid = true;
      errMsg.message = `Valid fd is 0.001-1000`;
      this.errorValidationObj.isValidFD = false;
    } else if (field === 'fd') {
      this.errorValidationObj.isValidFD = true;
    }
    if (field === 'fdv' && (value === null || value < 0.001 || value > 1000)) {
      errMsg.isValid = true;
      errMsg.message = 'Valid fdv is 0.001-1000';
      this.errorValidationObj.isValidFDV = false;
    } else if (field === 'fdv') {
      this.errorValidationObj.isValidFDV = true;
    }
    if (field === 'overhead' && (value === null || value < 0 || value > 255)) {
      errMsg.isValid = true;
      errMsg.message = 'Valid overhead is 0-255';
      this.errorValidationObj.isValidOverhead = false;
    } else if (field === 'overhead') {
      this.errorValidationObj.isValidOverhead = true;
    }
    if (element) {
      if (
        this.errorValidationObj.isValidDscp &&
        this.errorValidationObj.isValidName &&
        this.errorValidationObj.isValidUlr &&
        this.errorValidationObj.isValidFD &&
        this.errorValidationObj.isValidFDV &&
        this.errorValidationObj.isValidFrameLoss &&
        this.errorValidationObj.isValidOverhead
      ) {
        element.isValidConfig = true;
      } else {
        element.isValidConfig = false;
      }
    }
    this.onDataChange();
    return errMsg;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  bertFrameLabelDisplay(element: any): any {
    if (element.streamBertFrame?.indexOf('EMIX') !== -1) {
      element.sptFrameDisplay = 'EMIX';
    } else if (element.streamBertFrame?.indexOf('custom') !== -1) {
      element.sptFrameDisplay = element.streamBertFrame;
    } else {
      element.sptFrameDisplay = element.streamBertFrame;
    }
    return element.sptFrameDisplay;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  updateSptFrames(rowData: any): any {
    let sptFrames = rowData.streamBertFrame;
    const bertFrameList =
      rowData.framesList[this.parentConfig?.['ip-address-type']]['sptFrameList'];
    let index = 0;
    let isEmix = false;
    let isCustom = false;
    if (sptFrames?.indexOf('EMIX') !== -1 || sptFrames?.indexOf('custom') !== -1) {
      if (sptFrames?.indexOf('EMIX') !== -1) {
        isEmix = true;
        sptFrames = sptFrames?.split('EMIX');
      } else if (sptFrames?.indexOf('custom') !== -1) {
        isCustom = true;
      }
    }
    bertFrameList?.forEach(item => {
      index = index + 1;
      if (
        rowData.streamBertFrame &&
        rowData.streamBertFrame.includes(item['label']) &&
        !isEmix &&
        !isCustom
      ) {
        bertFrameList[index - 1]['enable'] = true;
      } else if (isCustom && item['label'] === 'custom') {
        bertFrameList[index - 1]['enable'] = true;
        bertFrameList[index - 1]['value'] = rowData.streamBertFrame.replace('custom:', '');
      } else if (isEmix && sptFrames?.length > 1 && bertFrameList[index - 1].label === 'EMIX') {
        bertFrameList[index - 1]['enable'] = true;
        bertFrameList[index - 1]['value'] = sptFrames[1]?.slice(1)?.split(':');
      } else {
        bertFrameList[index - 1]['enable'] = false;
      }
    });
    return bertFrameList;
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }
  // eslint-disable-next-line
  onDataChange(value?: number, index?: number, name?: string, element?: any): void {
    this.validateAllStreams = this.streamRows.map(item => item['isValidConfig']);
    //Rounding off upto 2 decimal for ULR/LOSS
    if (name == BertConfigFieldMapping.ULR || name == BertConfigFieldMapping.LOSS) {
      const rounded = value != null ? Math.round(value * 100) / 100 : null;
      value = rounded;
    }

    //Rounding off upto 3 decimal for FD/FDV
    if (name == BertConfigFieldMapping.FD || name == BertConfigFieldMapping.FDV) {
      const rounded = value != null ? Math.round(value * 1000) / 1000 : null;
      value = rounded;
    }
    if (name) {
      this.allStreamsConfig[index][name] = value;
    }
    if (this.validateAllStreams?.includes(false)) {
      this.onDataEmit(BertConfigFieldMapping.STREAMS, this.allStreamsConfig, false);
    } else {
      this.onDataEmit(BertConfigFieldMapping.STREAMS, this.allStreamsConfig, true);
    }
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  enforceMaxLength(event: any, maxLength: number) {
    const input = event.target;
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onBlurDecimalCleanup(element: any, key: string) {
    if (element[key] && element[key].toString().endsWith('.')) {
      element[key] = element[key].toString().slice(0, -1); // Remove trailing '.'
    }
    const raw = element[key];
    const value = parseFloat(raw);
    if (!isNaN(value)) {
      const isThreeDecimal = key === 'streamFD' || key === 'streamFDV';
      const factor = isThreeDecimal ? 1000 : 100;
      const rounded = Math.round(value * factor) / factor;
      element[key] = rounded.toString();
    } else {
      element[key] = '';
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  onClickFrameSize(element: any, index: number): void {
    if (this.vlanConfig !== vlanConfigValue.UNTAGGED) {
      element.framesList[this.ipAddressType]['selectedVlans'] =
        this.vlanConfig === vlanConfigValue.Q_IN_Q ? 2 : 1;
    }
    const frameDataObj = {
      rowData: element.framesList[this.ipAddressType],
      sptFrames: this.updateSptFrames(element),
      multiSelectFrames: element.framesList['multiSelectFrames'],
      emixFrames: element.framesList['emixFrames'],
      isMultiSelectEnable: element.isMultiSelectEnable
    };
    const dialogRef = this.dialog.open(FrameSizeSelectorComponent, {
      data: frameDataObj,
      disableClose: true
    });
    dialogRef.beforeClosed().subscribe(_res => {
      if (_res) {
        element.streamBertFrame = _res?.sptFrames;
        element['frame-list'] = element.framesList;
        this.onDataChange(
          element.streamBertFrame,
          index,
          BertConfigFieldMapping.BERT_FRAME_SIZE,
          element
        );
        this.onDataChange(element.framesList, index, BertConfigFieldMapping.FRAME_LIST, element);
        this.bertFrameLabelDisplay(element);
      }
    });
  }
}
